import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, MapPin, Video, Users, Loader2, Plus, CalendarPlus, Check, X, HelpCircle, Pencil } from 'lucide-react';
import AccessGate from '../components/AccessGate';
import MeetingForm from '../components/MeetingForm';
import { useAuth } from '../context/AuthContext';

const fmt = (dt) => { if (!dt) return ''; try { return new Date(dt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }); } catch { return dt; } };
const ModeBadge = ({ mode }) => {
  const map = { in_person: 'In person', online: 'Online', hybrid: 'Hybrid' };
  return <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--sage)', color: 'var(--navy)' }}>{map[mode] || mode}</span>;
};

const RSVP_OPTS = [
  { v: 'going', label: 'Going', icon: Check, color: '#1c6b2c' },
  { v: 'maybe', label: 'Maybe', icon: HelpCircle, color: '#9a6b00' },
  { v: 'not_going', label: "Can't", icon: X, color: '#8a1f11' },
];

function RsvpButtons({ meeting, onChange }) {
  const { rsvpMeeting } = useAuth();
  const [busy, setBusy] = useState(false);
  const pick = async (v) => {
    setBusy(true);
    try { const m = await rsvpMeeting(meeting.id, v); onChange && onChange(m); } finally { setBusy(false); }
  };
  return (
    <div className="inline-flex rounded-md overflow-hidden" style={{ border: '1px solid rgba(0,0,0,0.12)' }} data-testid={`rsvp-${meeting.id}`}>
      {RSVP_OPTS.map((o) => {
        const active = meeting.my_rsvp === o.v;
        const Icon = o.icon;
        return (
          <button key={o.v} disabled={busy} onClick={() => pick(o.v)} data-testid={`rsvp-${meeting.id}-${o.v}`}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-title border-r last:border-r-0"
            style={{ borderColor: 'rgba(0,0,0,0.12)', background: active ? o.color : 'white', color: active ? 'white' : 'var(--olive)' }}>
            <Icon size={13} /> {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default function Meetings() {
  const { listMeetings, meetingIcsUrl, canAccess, isAdmin } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // {} for new, meeting for edit
  const canManage = isAdmin || canAccess('admin-meetings') || meetings.some((m) => m.can_manage);

  const load = () => { setLoading(true); listMeetings().then(setMeetings).catch(() => setMeetings([])).finally(() => setLoading(false)); };
  useEffect(load, [listMeetings]);

  const now = Date.now();
  const upcoming = meetings.filter((m) => !m.starts_at || new Date(m.starts_at).getTime() >= now - 3600000);
  const past = meetings.filter((m) => m.starts_at && new Date(m.starts_at).getTime() < now - 3600000);
  const replace = (m) => setMeetings((cur) => cur.map((x) => (x.id === m.id ? m : x)));

  const Card = ({ m }) => (
    <div className="bg-white rounded-lg shadow-sm border p-5" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid={`meeting-${m.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/meetings/${m.id}`} className="font-title text-xl hover:underline" style={{ color: 'var(--navy)' }}>{m.title}</Link>
            <ModeBadge mode={m.mode} />
          </div>
          <div className="flex flex-wrap gap-4 text-sm mt-1.5" style={{ color: 'var(--olive)' }}>
            {m.starts_at && <span className="flex items-center gap-1"><CalendarClock size={14} /> {fmt(m.starts_at)}</span>}
            {m.location && <span className="flex items-center gap-1"><MapPin size={14} /> {m.location}</span>}
            {m.meeting_link && <span className="flex items-center gap-1"><Video size={14} /> Online link</span>}
            <span className="flex items-center gap-1"><Users size={14} /> {m.rsvp_summary.going} going</span>
          </div>
          {m.description && <p className="text-sm mt-2" style={{ color: 'var(--ink)' }}>{m.description}</p>}
        </div>
        {m.can_manage && (
          <button onClick={() => setForm(m)} className="flex-shrink-0 inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded" style={{ background: 'var(--sage)', color: 'var(--navy)' }} data-testid={`meeting-edit-${m.id}`}><Pencil size={13} /> Edit</button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <RsvpButtons meeting={m} onChange={replace} />
        <a href={meetingIcsUrl(m.id)} className="inline-flex items-center gap-1.5 text-sm hover:underline" style={{ color: 'var(--navy)' }} data-testid={`ics-${m.id}`}><CalendarPlus size={15} /> Add to calendar</a>
        <Link to={`/meetings/${m.id}`} className="text-sm hover:underline ml-auto" style={{ color: 'var(--maroon)' }} data-testid={`meeting-open-${m.id}`}>Agenda &amp; details →</Link>
      </div>
    </div>
  );

  return (
    <AccessGate pageKey="meetings">
      <div className="max-w-4xl mx-auto px-4 py-10 fade-in">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-title text-3xl md:text-4xl" style={{ color: 'var(--maroon)' }}>Meetings</h1>
            <div className="h-1 w-20 mt-2" style={{ background: 'var(--gold)' }} />
          </div>
          {canManage && (
            <button onClick={() => setForm({})} className="inline-flex items-center gap-2 px-4 py-2.5 rounded font-title text-sm" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="meeting-new-btn"><Plus size={16} /> Schedule meeting</button>
          )}
        </div>

        {loading ? <p className="text-sm mt-6 flex items-center gap-2" style={{ color: 'var(--olive)' }}><Loader2 size={15} className="animate-spin" /> Loading…</p>
          : meetings.length === 0 ? <p className="text-sm mt-6" style={{ color: 'var(--olive)' }} data-testid="meetings-empty">No meetings scheduled yet.</p>
          : (
            <div className="mt-6 space-y-8">
              <div>
                <h2 className="font-title text-lg mb-3" style={{ color: 'var(--navy)' }}>Upcoming</h2>
                {upcoming.length ? <div className="space-y-4" data-testid="meetings-upcoming">{upcoming.map((m) => <Card key={m.id} m={m} />)}</div>
                  : <p className="text-sm" style={{ color: 'var(--olive)' }}>Nothing coming up.</p>}
              </div>
              {past.length > 0 && (
                <div>
                  <h2 className="font-title text-lg mb-3" style={{ color: 'var(--navy)' }}>Past</h2>
                  <div className="space-y-4 opacity-75" data-testid="meetings-past">{past.map((m) => <Card key={m.id} m={m} />)}</div>
                </div>
              )}
            </div>
          )}
      </div>
      {form && <MeetingForm meeting={form.id ? form : null} onClose={() => setForm(null)} onSaved={load} />}
    </AccessGate>
  );
}
